<template>
  <div id="app" class="min-h-screen lg:flex">
    <a href="#main" class="skip-link">Skip to main content</a>

    <!-- Mobile bar. Below lg the sidebar is a drawer, so the mark and the two controls it
         needs stay here; the health dot moves into the drawer's footer. While the drawer is
         open this bar is inert: otherwise Tab walks into a page the overlay is covering and a
         screen reader still reads it. -->
    <header v-if="showChrome" class="border-b border-gray-200 bg-surface lg:hidden" :inert="mobileMenuOpen">
      <div class="flex h-14 items-center gap-2 px-3">
        <router-link to="/" class="flex min-w-0 items-center gap-2" :title="brandingName">
          <BrandMark class="h-7 w-7 shrink-0 text-accent" />
          <span class="truncate text-lg font-bold text-gray-900">{{ brandingName }}</span>
        </router-link>

        <div class="ml-auto flex shrink-0 items-center gap-1">
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-primary-500"
            :aria-label="themeLabel"
            :title="themeLabel"
            @click="setNextTheme"
          >
            <component :is="themeIcon" class="h-5 w-5" />
          </button>
          <button
            ref="menuButtonRef"
            type="button"
            class="inline-flex items-center justify-center rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-primary-500"
            :aria-expanded="mobileMenuOpen"
            aria-controls="mobile-nav"
            :aria-label="mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'"
            @click="mobileMenuOpen ? closeMobileMenu() : openMobileMenu()"
          >
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path v-if="!mobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </header>

    <!-- Desktop sidebar. It holds every destination, grouped by what the page is for, so
         the header row no longer has to fit the whole map: adding a page adds a row here
         instead of stealing width from the brand and the account block. -->
    <aside
      v-if="showChrome"
      class="hidden lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-60 lg:shrink-0 lg:flex-col lg:border-r lg:border-gray-200 lg:bg-surface"
    >
      <div class="flex h-14 shrink-0 items-center border-b border-gray-200 px-4">
        <router-link to="/" class="flex min-w-0 items-center gap-2" :title="brandingName">
          <BrandMark class="h-7 w-7 shrink-0 text-accent" />
          <span class="truncate text-lg font-bold text-gray-900">{{ brandingName }}</span>
        </router-link>
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain py-4">
        <NavSections :sections="navSections" />
      </div>

      <!-- The health dot used to be a 2px sliver with an sr-only label in the old header;
           the sidebar footer has room for the words. -->
      <div class="shrink-0 border-t border-gray-200 px-4 py-3">
        <p class="flex items-center gap-2 text-xs text-gray-500" :title="healthLabel">
          <span
            class="h-2 w-2 shrink-0 rounded-full"
            :class="healthStatus === 'ok' ? 'bg-success-500' : 'bg-danger-500'"
          ></span>
          {{ healthLabel }}
        </p>
      </div>
    </aside>

    <!-- Content column -->
    <div class="flex min-w-0 flex-1 flex-col" :inert="mobileMenuOpen">
      <!-- Utility bar. Deliberately not sticky: the tables' own sticky headers also pin to
           top-0, and a sticky bar would hide them. -->
      <header
        v-if="showChrome"
        class="hidden h-14 shrink-0 items-center gap-4 border-b border-gray-200 bg-surface px-6 lg:flex"
      >
        <!-- Organisation is a scope, not a preference, so it stays a visible native select
             rather than going behind the account menu. The name is capped: it is deployment
             data, and an uncapped select was the widest thing in the old header (333px for a
             long org name). -->
        <div v-if="showOrgSwitcher" class="flex min-w-0 items-center gap-2">
          <label for="org-switcher" class="shrink-0 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Organisation
          </label>
          <select
            id="org-switcher"
            class="max-w-[15rem] truncate rounded-md border border-gray-300 bg-surface px-2 py-1.5 text-sm text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-primary-500"
            :value="activeOrganizationId ?? ''"
            :title="activeOrgName"
            @change="onSwitchOrg"
          >
            <option v-if="isSuperAdmin" value="">All organisations</option>
            <option v-for="org in organizations" :key="org.id" :value="org.id">
              {{ org.name }}
            </option>
          </select>
        </div>

        <div class="ml-auto flex shrink-0 items-center gap-1">
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-primary-500"
            :aria-label="themeLabel"
            :title="themeLabel"
            @click="setNextTheme"
          >
            <component :is="themeIcon" class="h-5 w-5" />
          </button>

          <!-- Session block: email, role, Account settings and Sign out all live one level
               down, because the three of them were ~347px of a row that had ~377px too much
               in it. The trigger is the account destination itself, not a dead avatar. -->
          <div ref="accountMenuRef" class="relative">
            <button
              ref="accountButtonRef"
              type="button"
              class="inline-flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 focus:outline-hidden focus:ring-2 focus:ring-primary-500"
              aria-haspopup="true"
              aria-controls="account-menu"
              :aria-expanded="accountMenuOpen"
              :aria-label="`Account menu for ${userEmail}`"
              @click="accountMenuOpen = !accountMenuOpen"
            >
              <span
                class="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-xs font-semibold text-white"
                aria-hidden="true"
              >
                {{ initials }}
              </span>
              <component
                :is="chevronDownIcon"
                class="h-4 w-4 transition-transform duration-200"
                :class="{ 'rotate-180': accountMenuOpen }"
              />
            </button>

            <div v-if="accountMenuOpen" id="account-menu" class="absolute right-0 top-full z-30 mt-1 w-64 p-1 card">
              <div class="px-3 py-2">
                <p class="truncate text-sm font-medium text-gray-900" :title="userEmail">{{ userEmail }}</p>
                <p class="text-xs text-gray-500">{{ roleLabel }}</p>
              </div>
              <div class="my-1 border-t border-gray-200"></div>
              <router-link
                :to="{ name: 'Account' }"
                class="block rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                :class="{ 'bg-primary-100 text-primary-800': $route.name === 'Account' }"
                :aria-current="$route.name === 'Account' ? 'page' : undefined"
                @click="accountMenuOpen = false"
              >
                Account settings
              </router-link>
              <button
                type="button"
                class="block w-full rounded-md px-3 py-2 text-left text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                @click="signOut"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      <main id="main" class="min-w-0 flex-1">
        <router-view />
      </main>
    </div>

    <!-- Mobile drawer: the same sections as the sidebar, plus the session block that the
         desktop header keeps in its account menu. -->
    <div v-if="showChrome && mobileMenuOpen" class="lg:hidden">
      <div class="fixed inset-0 z-40 bg-gray-900/50" aria-hidden="true" @click="closeMobileMenu"></div>
      <div
        id="mobile-nav"
        ref="mobileNavRef"
        tabindex="-1"
        class="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col border-r border-gray-200 bg-surface shadow-pop focus:outline-hidden"
      >
        <div class="flex h-14 shrink-0 items-center gap-2 border-b border-gray-200 px-3">
          <router-link to="/" class="flex min-w-0 items-center gap-2" :title="brandingName">
            <BrandMark class="h-7 w-7 shrink-0 text-accent" />
            <span class="truncate text-lg font-bold text-gray-900">{{ brandingName }}</span>
          </router-link>
          <button
            type="button"
            class="ml-auto inline-flex items-center justify-center rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-primary-500"
            aria-label="Close navigation menu"
            @click="closeMobileMenu"
          >
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain py-4">
          <NavSections :sections="navSections" @navigate="closeMobileMenu" />
        </div>

        <div class="shrink-0 space-y-3 border-t border-gray-200 px-4 py-3">
          <p class="flex items-center gap-2 text-xs text-gray-500" :title="healthLabel">
            <span
              class="h-2 w-2 shrink-0 rounded-full"
              :class="healthStatus === 'ok' ? 'bg-success-500' : 'bg-danger-500'"
            ></span>
            {{ healthLabel }}
          </p>
          <div class="min-w-0">
            <p class="truncate text-sm font-medium text-gray-900" :title="userEmail">{{ userEmail }}</p>
            <p class="text-xs text-gray-500">{{ roleLabel }}</p>
          </div>
          <div class="flex items-center gap-2">
            <router-link :to="{ name: 'Account' }" class="btn btn-secondary text-sm">Account</router-link>
            <button type="button" class="btn btn-secondary text-sm" @click="signOut">Sign out</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Global loading overlay -->
    <div v-if="globalLoading" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-surface rounded-card p-6 flex items-center space-x-3">
        <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        <span class="text-gray-700">Loading...</span>
      </div>
    </div>

    <ToastHost />
  </div>
</template>

<script setup lang="ts">
import { computed, h, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { healthApi } from '@/api/client';
import { useAuth } from '@/composables/useAuth';
import { useBranding } from '@/composables/useBranding';
import { useTheme } from '@/composables/useTheme';
import type { NavLink, NavSection } from '@/types/nav';
import BrandMark from '@/components/BrandMark.vue';
import NavSections from '@/components/NavSections.vue';
import ToastHost from '@/components/ToastHost.vue';

// State
const mobileMenuOpen = ref(false);
const accountMenuOpen = ref(false);
const menuButtonRef = ref<HTMLButtonElement | null>(null);
const mobileNavRef = ref<HTMLElement | null>(null);
const accountMenuRef = ref<HTMLElement | null>(null);
const accountButtonRef = ref<HTMLButtonElement | null>(null);
const healthStatus = ref<'ok' | 'error'>('ok');
const globalLoading = ref(false);

const route = useRoute();
const router = useRouter();
const {
  state,
  isAdmin,
  isOrgAdmin,
  organizations,
  activeOrganizationId,
  isSuperAdmin,
  switchOrganization,
  logout,
} = useAuth();

// The login screen is the only route without the shell.
const showChrome = computed(() => route.name !== 'Login');

/**
 * Every destination, grouped by the question it answers. The order inside a group is the
 * order of the work: what arrived, who sent it, what is wrong, which domains, what needs
 * attention. Admin pages are derived from the role rather than declared per role, so a
 * viewer simply gets a shorter sidebar instead of a second hardcoded list.
 */
const navSections = computed<NavSection[]>(() => {
  const sections: NavSection[] = [
    {
      label: 'Analyse',
      links: [
        { label: 'Dashboard', to: '/', activeFor: ['Dashboard'] },
        { label: 'Reports', to: '/reports', activeFor: ['Reports', 'ReportDetail'] },
        // Per-sender view of the same reports: "who is sending as us" is a different
        // question from "which reports arrived", so it gets its own page.
        { label: 'Sources', to: '/sources', activeFor: ['Sources'] },
        { label: 'Forensics', to: '/forensics', activeFor: ['Forensics', 'ForensicDetail'] },
        { label: 'Domains', to: '/domains', activeFor: ['Domains'] },
        // Operational inbox: it is only useful if it is seen without opening a menu.
        { label: 'Alerts', to: '/alerts', activeFor: ['Alerts'] },
      ],
    },
    {
      label: 'Operations',
      links: [
        { label: 'Processing logs', to: '/processing-logs', activeFor: ['ProcessingLogs'] },
        // SMTP TLS outcomes: a separate report type from DMARC, so it gets its own page.
        { label: 'TLS reports', to: '/tls-reports', activeFor: ['TlsRpt'] },
      ],
    },
  ];

  const adminLinks: NavLink[] = [];
  // Per-organisation administration. A superadmin needs one organisation selected for it
  // to mean anything, so the link waits for that choice.
  if (isOrgAdmin.value && (!isSuperAdmin.value || activeOrganizationId.value)) {
    adminLinks.push({ label: 'Organisation', to: '/organisation', activeFor: ['Organisation'] });
  }
  if (isAdmin.value) {
    adminLinks.push(
      { label: 'Settings', to: '/admin/settings', activeFor: ['AdminSettings'] },
      { label: 'Users', to: '/admin/users', activeFor: ['AdminUsers'] },
      { label: 'Identity providers', to: '/admin/providers', activeFor: ['AdminProviders'] },
      { label: 'Audit log', to: '/admin/audit', activeFor: ['AdminAudit'] },
    );
  }
  if (adminLinks.length) {
    sections.push({ label: 'Administration', links: adminLinks });
  }

  // The glossary is for everyone: it explains the vocabulary the reports and checks use.
  sections.push({
    label: 'Reference',
    links: [{ label: 'Glossary', to: '/glossary', activeFor: ['Glossary'] }],
  });

  return sections;
});

const showOrgSwitcher = computed(
  () => !!state.user && (organizations.value.length > 1 || isSuperAdmin.value),
);

const activeOrgName = computed(() => {
  if (!activeOrganizationId.value) return 'All organisations';
  return organizations.value.find((org) => org.id === activeOrganizationId.value)?.name ?? 'Organisation';
});

// Destructured to top-level bindings rather than a `branding` object: only setup top-level
// refs are auto-unwrapped in the template.
const { name: brandingName, load: loadBranding } = useBranding();
loadBranding();

const { theme, nextTheme, setNextTheme } = useTheme();

const userEmail = computed(() => state.user?.email ?? '');

// Avatar text: the mark has to fit 32px, so a name gives its two initials and an email
// gives its first letter. The full address stays in the menu and in the trigger's label.
const initials = computed(() => {
  const name = state.user?.name?.trim();
  if (name) {
    const parts = name.split(/\s+/);
    return `${parts[0][0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase();
  }
  return (userEmail.value[0] ?? '?').toUpperCase();
});

const roleLabel = computed(() => {
  if (isSuperAdmin.value) return 'Administrator';
  return state.orgRole === 'admin' ? 'Organisation admin' : 'Viewer';
});

const onSwitchOrg = (event: Event) => {
  const value = (event.target as HTMLSelectElement).value;
  switchOrganization(value || null);
};

const themeIcons = {
  system: () =>
    h('svg', { fill: 'none', viewBox: '0 0 24 24', stroke: 'currentColor', 'stroke-width': 1.8, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, [
      h('rect', { x: 3, y: 4, width: 18, height: 12, rx: 2 }),
      h('path', { d: 'M8 20h8M12 16v4' }),
    ]),
  light: () =>
    h('svg', { fill: 'none', viewBox: '0 0 24 24', stroke: 'currentColor', 'stroke-width': 1.8, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, [
      h('circle', { cx: 12, cy: 12, r: 4 }),
      h('path', { d: 'M12 2v2m0 16v2M2 12h2m16 0h2M4.9 4.9l1.4 1.4m11.4 11.4 1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4' }),
    ]),
  dark: () =>
    h('svg', { fill: 'none', viewBox: '0 0 24 24', stroke: 'currentColor', 'stroke-width': 1.8, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, [
      h('path', { d: 'M21 12.8A8.5 8.5 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z' }),
    ]),
};

const themeIcon = computed(() => themeIcons[theme.value]);
const themeLabel = computed(
  () => `Theme: ${theme.value}. Switch to ${nextTheme.value}.`,
);

// The chevron rotates via the template's class binding; declared with h() to match the
// theme icons rather than adding another inline svg to the header markup.
const chevronDownIcon = () =>
  h(
    'svg',
    {
      fill: 'none',
      viewBox: '0 0 24 24',
      stroke: 'currentColor',
      'stroke-width': 2,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    },
    [h('path', { d: 'm6 9 6 6 6-6' })],
  );

// Health wording lives in one place; the sidebar footer and the drawer both read it.
const healthLabel = computed(() =>
  healthStatus.value === 'ok' ? 'Parser online' : 'Parser offline',
);

const signOut = async () => {
  await logout();
  await router.push({ name: 'Login' });
};

// --- Drawer -------------------------------------------------------------------------
// A fixed panel over a scrolling page: lock the page behind it, and put focus inside so
// Escape and Tab start in the menu rather than on the page underneath.
const openMobileMenu = async () => {
  mobileMenuOpen.value = true;
  await nextTick();
  mobileNavRef.value?.focus();
};

const closeMobileMenu = () => {
  mobileMenuOpen.value = false;
  menuButtonRef.value?.focus();
};

// Rotating a tablet past 1024 hides the drawer with CSS but would leave the page locked and
// the trigger still reporting an open menu.
const desktopQuery = window.matchMedia('(min-width: 1024px)');
const onDesktopChange = (event: MediaQueryListEvent) => {
  if (event.matches) mobileMenuOpen.value = false;
};

watch(mobileMenuOpen, (open) => {
  document.body.classList.toggle('overflow-hidden', open);
});

// --- Disclosures --------------------------------------------------------------------
// Closing on route change means a click on a panel item can never leave the panel hanging
// over the page it just navigated to.
watch(
  () => route.fullPath,
  () => {
    accountMenuOpen.value = false;
    mobileMenuOpen.value = false;
  },
);

// The panels are disclosures, not focus traps, so pointer-outside and Escape both dismiss
// them (and Escape hands focus back to the trigger). Bound on document because a click on
// the page body must close them too.
const onDocumentPointerDown = (event: MouseEvent) => {
  if (accountMenuRef.value && !accountMenuRef.value.contains(event.target as Node)) {
    accountMenuOpen.value = false;
  }
};

const onDocumentKeydown = (event: KeyboardEvent) => {
  if (event.key !== 'Escape') return;
  if (accountMenuOpen.value) {
    accountMenuOpen.value = false;
    accountButtonRef.value?.focus();
  } else if (mobileMenuOpen.value) {
    closeMobileMenu();
  }
};

onMounted(() => {
  document.addEventListener('pointerdown', onDocumentPointerDown);
  document.addEventListener('keydown', onDocumentKeydown);
  desktopQuery?.addEventListener('change', onDesktopChange);
});

onUnmounted(() => {
  document.removeEventListener('pointerdown', onDocumentPointerDown);
  document.removeEventListener('keydown', onDocumentKeydown);
  desktopQuery?.removeEventListener('change', onDesktopChange);
  document.body.classList.remove('overflow-hidden');
  stopHealthChecks();
});

// Health check
const checkHealth = async () => {
  if (!state.user) {
    return;
  }

  try {
    await healthApi.check();
    healthStatus.value = 'ok';
  } catch (error) {
    healthStatus.value = 'error';
    console.warn('Health check failed:', error);
  }
};

// Periodic health check
let healthCheckInterval: ReturnType<typeof setInterval> | undefined;

const startHealthChecks = () => {
  stopHealthChecks();
  checkHealth();
  healthCheckInterval = setInterval(checkHealth, 30000);
};

const stopHealthChecks = () => {
  if (healthCheckInterval) {
    clearInterval(healthCheckInterval);
    healthCheckInterval = undefined;
  }
};

// Only poll while there is a session: /health is public, but an anonymous login page
// has no reason to report the parser's state, and it keeps the noise down.
watch(
  () => state.user,
  (user) => (user ? startHealthChecks() : stopHealthChecks()),
  { immediate: true }
);
</script>
